import {
  MigrateBaBCustomerFromLaravelToPhoenixInputSchema,
  MigrateBaBCustomerFromLaravelToPhoenixOutputSchema,
} from '@lyka-pet-food/contracts'
import {
  AddressSchema,
  PasswordChangeResponseSchema,
  PatchUserSchema,
  PhoenixMigrateAndAuthenticateBodySchema,
  PutDefaultAddressSchema,
  UserSchema,
} from '@repo/api-types'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { ErrorResponseSchema } from './general'

const c = initContract()

export const authContract = c.router({
  login: {
    method: 'POST',
    path: '/login',
    responses: {
      200: z.object({ accessToken: z.string(), refreshToken: z.string() }),
      401: ErrorResponseSchema,
    },
    body: z.object({
      email: z.string(),
      password: z.string(),
    }),
  },
  phoenixMigrateAndAuthenticate: {
    method: 'POST',
    path: '/phoenix-migrate-and-authenticate',
    body: PhoenixMigrateAndAuthenticateBodySchema,
    responses: {
      200: z.object({ redirectUrl: z.string() }),
      502: ErrorResponseSchema,
    },
  },
  forgotPassword: {
    method: 'POST',
    path: '/forgot-password',
    responses: {
      200: z.object({ success: z.boolean() }),
      401: ErrorResponseSchema,
    },
    body: z.object({
      email: z.string(),
    }),
  },
  confirmForgotPassword: {
    method: 'POST',
    path: '/confirm-forgot-password',
    responses: {
      200: z.object({ success: z.boolean() }),
      401: ErrorResponseSchema,
    },
    body: z.object({
      email: z.string(),
      password: z.string(),
      confirmationCode: z.string(),
    }),
  },
  resetPassword: {
    method: 'POST',
    path: '/reset-password',
    responses: {
      200: PasswordChangeResponseSchema,
      400: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
    body: z.object({
      previousPassword: z.string(),
      password: z.string(),
    }),
  },
  updatePassword: {
    method: 'POST',
    path: '/update-password',
    responses: {
      200: PasswordChangeResponseSchema,
      400: ErrorResponseSchema,
    },
    body: z.object({
      newPassword: z.string(),
    }),
  },
  refresh: {
    method: 'GET',
    path: '/refresh',
    responses: {
      200: z.object({ accessToken: z.string() }),
      400: ErrorResponseSchema,
    },
  },
  logout: {
    method: 'POST',
    path: '/logout',
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    /**
     * The stardard practice indicates that refreshToken is passed as cookie for webapps.
     * For iOS, however, the standard practice is to pass refreshToken as body as
     * they do not manage cookies the same way as webapps.
     *
     * If both are present, cookie wins over the refreshToken body.
     */
    body: z.object({ refreshToken: z.string().optional() }),
  },
  signOutEverywhere: {
    method: 'POST',
    path: '/sign-out-everywhere',
    responses: {
      200: z.object({ success: z.boolean() }),
    },
    body: z.void(),
  },
  getMe: {
    method: 'GET',
    path: '/me',
    responses: {
      200: UserSchema,
      400: ErrorResponseSchema,
    },
  },
  patchMe: {
    method: 'PATCH',
    path: '/me',
    body: PatchUserSchema,
    responses: {
      200: UserSchema,
      400: ErrorResponseSchema,
    },
  },

  getDefaultCustomerAddress: {
    method: 'GET',
    path: '/addresses/default',
    responses: {
      200: AddressSchema,
      400: ErrorResponseSchema,
    },
  },
  updateDefaultCustomerAddress: {
    method: 'PUT',
    path: '/addresses/default',
    body: PutDefaultAddressSchema,
    responses: {
      200: z.object({ success: z.boolean() }),
      400: ErrorResponseSchema,
    },
  },
  isEmailTaken: {
    method: 'GET',
    path: '/check-email',
    query: z.object({
      email: z.string().email(),
    }),
    responses: {
      200: z.object({ isTaken: z.boolean() }),
      400: ErrorResponseSchema,
      401: ErrorResponseSchema,
    },
  },
  migrateBaBCustomerFromLaravelToPhoenix: {
    method: 'POST',
    path: '/bab/migrate-from-laravel',
    body: MigrateBaBCustomerFromLaravelToPhoenixInputSchema,
    responses: {
      200: MigrateBaBCustomerFromLaravelToPhoenixOutputSchema,
      400: ErrorResponseSchema,
      502: ErrorResponseSchema,
    },
  },
})
